import './ReProductionGenerate.scss';
import {
  Box,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
} from '@mui/material';
import {t} from 'i18next';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  useEffect,
  forwardRef,
  useImperativeHandle,
  useState,
  useRef,
  useMemo,
} from 'react';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrModal from '../../../common/arr-modal/ArrModal';
import {ReactComponent as RadioActiveIcon} from '../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../assets/images/radio-enable-icon.svg';

import {
  GetReProductionGenerateViewRequestParam,
  getReProductionGenerateStatusParams,
  GetReProductionExecuteGenerateRequestBody,
  GetReProductionExecutePlaceholderingRequestBody,
  GetReProductionExportBatesRangeRequestParam,
} from '../../../../api/types/reproduction.type';
import {
  RegenerateType,
  ProductionStatus,
  ProductionType,
  ProductionState,
  FormAction,
  ToastType,
} from '../../create-new-production/CreateNewProductionUtils';
import {
  removeParenthesisSpaces,
  parseStatusText,
} from '../../create-new-production/CreateNewProductionHelpers';
import {
  ProductionGenerateFormData,
  productionGenerateSchema,
} from '../../../../schemas/productions/productionSchema';
import ReProductionErrorGridModal from './ErrorModal/ReProductionErrorGridModal';
import {ErrorGridType} from '../../create-new-production/generate/ErrorModal/utils';
import {
  canProceedToNextStep,
  getReProductionStatusFromStateCode,
  isAllowedNextStepStateWhilePolling,
  isReProductionCommitWorkflowFailedState,
  isReProductionCommitWorkflowInProgressState,
  isReProductionPostQcCheckComplete,
  ModuleType,
  ReProductionFormData,
} from '../ReProductionUtils';
import {useDispatch} from 'react-redux';
import {setReproductionIsFieldsChanged} from '../../../../redux/slice/reproductionSlice';
import {
  useGetReProductionExportBatesRange,
  useGetReProductionGenerateStatus,
  useGetReProductionGenerateView,
  useReProductionExecuteGenerate,
  usereProductionExecutePlaceholdering,
} from '../../../../hooks/productions/useReProductions';

const DEFAULT_DOCS_GENERATED_PLACEHOLDER = '---';
const DEFAULT_BATES_RANGE_PLACEHOLDER = '---';
const DEFAULT_POLLING_INTERVAL_MS = 5000;
const GENERATE_VIEW_UPDATE_DELAY_MS = 1000;
const STATUS_CHECK_DELAY_MS = 1500;

// Hardcoded - to be removed
//543 for export button
// place holer error button - 24 amit project
// FAILED - 484

interface ReProductionGenerateProps {
  securityGroupId: number;
  productionSetId: number;
  formData: ReProductionFormData;
  onFormDataChange: (updates: Partial<ReProductionFormData>) => void;
  getStatus: (isShowToast: boolean, status: ToastType, message: string) => void;
  goNext: () => void;
  goBack: () => void;
  onStateChange: (state: {
    saveDisabled: boolean;
    nextDisabled: boolean;
  }) => void;
  onWizardStatusChange?: (status: string) => void;
  onCommitUncommitStateChange?: (stateCode: string) => void;
  productionStatusCode?: string;
  isWizardEditableAfterUncommit?: boolean;
}

const ReProductionGenerate = forwardRef<
  {save: () => void; next: () => void},
  ReProductionGenerateProps
>(
  (
    {
      formData,
      securityGroupId = 0,
      getStatus,
      goNext,
      onStateChange,
      onWizardStatusChange,
      onCommitUncommitStateChange,
      productionStatusCode = '',
      isWizardEditableAfterUncommit = false,
    },
    ref
  ) => {
    const dispatch = useDispatch();
    const reProductionId = formData.productionId;
    const moduleType = ModuleType.REPRODUCTION;

    const [isPolling, setIsPolling] = useState(false);
    const [isOperationInProgress, setIsOperationInProgress] = useState(false);
    const statusCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
      null
    );
    const lastProcessedStatusRef = useRef<string | null>(null);
    const [showRegenerateModal, setShowRegenerateModal] = useState(false);
    const [selectedRegenerateType, setSelectedRegenerateType] =
      useState<RegenerateType>(RegenerateType.RegenerateAll);
    const [showPlaceholderErrorDocsModal, setShowPlaceholderErrorDocsModal] =
      useState(false);
    const [openErrorGridModal, setOpenErrorGridModal] = useState(false);
    const [gridType, setGridType] = useState<ErrorGridType>(
      ErrorGridType.ProductionErrors
    );

    const {
      handleSubmit,
      formState: {isSubmitting, isValid},
    } = useForm<ProductionGenerateFormData>({
      mode: 'onChange',
      resolver: zodResolver(productionGenerateSchema()),
      defaultValues: {
        action: FormAction.NEXT,
      },
    });

    const isGenerateViewEnabled = reProductionId > 0 && !isPolling;

    const generateViewParams = useMemo<GetReProductionGenerateViewRequestParam>(
      () => ({
        moduletype: moduleType,
        SecurityGroupId: securityGroupId,
        reProductionId,
      }),
      [moduleType, securityGroupId, reProductionId]
    );

    const {data: generateViewData} = useGetReProductionGenerateView(
      generateViewParams,
      isGenerateViewEnabled
    );

    const pollingInterval =
      generateViewData?.reProductionGeneratePollingInterval ||
      DEFAULT_POLLING_INTERVAL_MS;

    const statusParams = useMemo<getReProductionGenerateStatusParams>(
      () => ({
        SecurityGroupId: securityGroupId,
        moduletype: moduleType,
        reProductionId,
      }),
      [moduleType, securityGroupId, reProductionId]
    );

    const isStatusPollingEnabled = isPolling && reProductionId > 0;

    const {data: statusData} = useGetReProductionGenerateStatus(
      statusParams,
      isStatusPollingEnabled,
      isStatusPollingEnabled ? pollingInterval : false
    );

    const executeGenerateMutation = useReProductionExecuteGenerate(
      (data) => {
        getStatus?.(true, ToastType.Success, data.message || '');
        setIsOperationInProgress(true);
        setIsPolling(true);
        onWizardStatusChange?.(ProductionStatus.INPROGRESS);
      },
      (error) => {
        getStatus?.(true, ToastType.Error, error.message || '');
        setIsOperationInProgress(false);
        setIsPolling(false);
      }
    );

    const executePlaceholderingMutation = usereProductionExecutePlaceholdering(
      (data) => {
        getStatus?.(true, ToastType.Success, data.messageForUser || '');
        setIsOperationInProgress(true);
        setIsPolling(true);
        onWizardStatusChange?.(ProductionStatus.INPROGRESS);
      },
      (error) => {
        getStatus?.(true, ToastType.Error, error.message || '');
        setIsOperationInProgress(false);
        setIsPolling(false);
      }
    );

    const productionGenerateDetails =
      generateViewData?.reProductionGenerateDetails;

    const currentStatus = statusData?.reProductionCurrentStatus;
    const currentStatusText = currentStatus?.reProductionStatusText;
    const currentStatusCode =
      currentStatus?.reProductionStatus ??
      currentStatus?.reProductionStatusCode;
    const currentState = currentStatus?.reProductionState;
    const latestProductionState = isPolling
      ? (currentState ?? productionGenerateDetails?.productionStateCode)
      : productionGenerateDetails?.productionStateCode;
    const mappedStatusFromState = getReProductionStatusFromStateCode(
      latestProductionState
    );
    const rawDisplayStatus =
      isPolling && currentStatusText
        ? currentStatusText
        : productionGenerateDetails?.productionGenerateStatus ||
          t('reProduction.generate.draftStatus');
    const displayStatus = mappedStatusFromState ?? rawDisplayStatus;

    const displayBatesRange = isPolling
      ? currentStatus?.generatedBatesRange || DEFAULT_BATES_RANGE_PLACEHOLDER
      : productionGenerateDetails?.generatedBatesRange ||
        DEFAULT_BATES_RANGE_PLACEHOLDER;

    const docsGenerated = isPolling
      ? currentStatus?.completedDocCount?.toString() ||
        DEFAULT_DOCS_GENERATED_PLACEHOLDER
      : productionGenerateDetails?.completedDocCount?.toString() ||
        DEFAULT_DOCS_GENERATED_PLACEHOLDER;

    const producitonDocsCompleteText = removeParenthesisSpaces(
      currentStatus?.reProducitonDocsComplete
    );
    const parsedStatus = parseStatusText(displayStatus);

    // Use status API only while polling; otherwise stale cached status can
    // incorrectly lock Regenerate / Export Bates after generation completes.
    const liveStatus = isPolling
      ? (currentStatusCode ??
        productionGenerateDetails?.productionStatusCode1 ??
        productionStatusCode)
      : (productionGenerateDetails?.productionStatusCode1 ??
        productionStatusCode);

    // Match production Generate: lock actions only when in progress/completed
    // and not in post-generation QC complete state.
    //removed ProductionState.CANCELFAILED check as cancel feature is not applicable to reproduction
    const isInProgressOrCompleted =
      (liveStatus === ProductionStatus.COMPLETED ||
        liveStatus === ProductionStatus.INPROGRESS) &&
      latestProductionState !== ProductionState.QCCOMPLETE;

    const isQCComplete =
      latestProductionState === ProductionState.QCCOMPLETE ||
      latestProductionState === ProductionState.COMPLETED;
    const isPostQcCheckComplete = isReProductionPostQcCheckComplete(
      latestProductionState,
      displayStatus
    );
    const isCommitWorkflowState =
      isReProductionCommitWorkflowInProgressState(latestProductionState) ||
      isReProductionCommitWorkflowFailedState(latestProductionState);
    const isCommitWorkflowFailedState = isReProductionCommitWorkflowFailedState(
      latestProductionState
    );
    const isRegenerateDisabled =
      isInProgressOrCompleted || isCommitWorkflowFailedState;
    const isGenerationStillRunning =
      !isWizardEditableAfterUncommit &&
      (productionStatusCode === ProductionStatus.INPROGRESS ||
        liveStatus === ProductionStatus.INPROGRESS) &&
      !isCommitWorkflowState &&
      !isPostQcCheckComplete;
    const isValidStateForNext =
      isPostQcCheckComplete ||
      isCommitWorkflowState ||
      (!isGenerationStillRunning &&
        canProceedToNextStep(latestProductionState));
    const canProceedWhilePollingForState = isAllowedNextStepStateWhilePolling(
      latestProductionState
    );
    const isStateAllowedWhilePolling =
      isQCComplete || canProceedWhilePollingForState || isPostQcCheckComplete;

    useEffect(() => {
      if (isWizardEditableAfterUncommit) {
        onWizardStatusChange?.('');
        onCommitUncommitStateChange?.('');
        return;
      }

      onCommitUncommitStateChange?.(
        isCommitWorkflowState ? (latestProductionState ?? '') : ''
      );

      if (
        latestProductionState === ProductionState.UNCOMMITED ||
        latestProductionState === ProductionState.QCCOMPLETE
      ) {
        onWizardStatusChange?.('');
        return;
      }
      if (isCommitWorkflowState) {
        onWizardStatusChange?.(ProductionStatus.INPROGRESS);
        return;
      }
      if (liveStatus === ProductionStatus.INPROGRESS) {
        onWizardStatusChange?.(ProductionStatus.INPROGRESS);
        return;
      }
      if (liveStatus === ProductionStatus.COMPLETED) {
        onWizardStatusChange?.(ProductionStatus.COMPLETED);
      }
    }, [
      liveStatus,
      latestProductionState,
      isCommitWorkflowState,
      onWizardStatusChange,
      onCommitUncommitStateChange,
      isWizardEditableAfterUncommit,
    ]);

    const canShowPlaceholderErrorDocsButton =
      productionGenerateDetails?.canContinueWithPlacholdering ?? false;
    const canShowGenerateButton =
      productionGenerateDetails?.canGenerate ?? false;
    const canShowRegenerateButton =
      productionGenerateDetails?.canRegenerate ?? false;
    const shouldContinueGeneration =
      productionGenerateDetails?.shouldContinue ?? false;
    const hasDisplayBatesRange =
      !!displayBatesRange &&
      displayBatesRange !== DEFAULT_BATES_RANGE_PLACEHOLDER;
    const isPostGenerationExportState = [
      ProductionState.QCCOMPLETE,
      ProductionState.GENERATECOMPLETE,
      ProductionState.COMPLETED,
    ].includes(latestProductionState as ProductionState);
    const canShowExportBatesButton =
      hasDisplayBatesRange || (!isPolling && isPostGenerationExportState);
    const canShowNativelyPlaceholderErrorDocs =
      (productionGenerateDetails?.nativelyGeneratedDocs ?? 0) > 0;

    const automatedChecks = [
      {
        name: productionGenerateDetails?.automatedCheckStep1Name,
        status: productionGenerateDetails?.automatedCheckStep1Status,
        className: productionGenerateDetails?.automatedCheckStep1ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep2Name,
        status: productionGenerateDetails?.automatedCheckStep2Status,
        className: productionGenerateDetails?.automatedCheckStep2ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep3Name,
        status: productionGenerateDetails?.automatedCheckStep3Status,
        className: productionGenerateDetails?.automatedCheckStep3ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep4Name,
        status: productionGenerateDetails?.automatedCheckStep4Status,
        className: productionGenerateDetails?.automatedCheckStep4ClassName,
      },
      {
        name: productionGenerateDetails?.automatedCheckStep5Name,
        status: productionGenerateDetails?.automatedCheckStep5Status,
        className: productionGenerateDetails?.automatedCheckStep5ClassName,
      },
    ].filter((c) => c.name);

    useImperativeHandle(ref, () => ({
      save: () => {
        dispatch(setReproductionIsFieldsChanged({isFieldsChanged: false}));
      },
      next: () => {
        if (isValidStateForNext) {
          goNext?.();
        }
      },
    }));

    useEffect(() => {
      const details = generateViewData?.reProductionGenerateDetails;
      if (!details || reProductionId <= 0) return;
      if (details.shouldContinue) return;

      if (
        details.productionStatusCode1 === ProductionStatus.INPROGRESS &&
        details.productionStateCode !== ProductionState.QCCOMPLETE &&
        details.productionStateCode !== ProductionState.GENERATECOMPLETE &&
        details.productionStateCode !== ProductionState.UNCOMMITED &&
        !isReProductionPostQcCheckComplete(
          details.productionStateCode,
          details.productionGenerateStatus
        )
      ) {
        setIsPolling(true);
        onWizardStatusChange?.(ProductionStatus.INPROGRESS);
      }
    }, [generateViewData, reProductionId, onWizardStatusChange]);

    // Polling itself is driven by React Query's refetchInterval on the status
    // query (see isStatusPollingEnabled). This effect only resets the
    // processed-status guard and cleans up the stop-check timeout.
    useEffect(() => {
      if (!isPolling || reProductionId <= 0) {
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
        lastProcessedStatusRef.current = null;
        return;
      }

      lastProcessedStatusRef.current = null;

      return () => {
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
      };
    }, [isPolling, reProductionId]);

    useEffect(() => {
      if (
        executeGenerateMutation.isPending ||
        executePlaceholderingMutation.isPending ||
        executeGenerateMutation.isSuccess ||
        executePlaceholderingMutation.isSuccess
      ) {
        setIsOperationInProgress(true);
      }
    }, [
      executeGenerateMutation.isPending,
      executePlaceholderingMutation.isPending,
      executeGenerateMutation.isSuccess,
      executePlaceholderingMutation.isSuccess,
    ]);

    useEffect(() => {
      if (isOperationInProgress && !isPolling && generateViewData) {
        const timer = setTimeout(() => {
          setIsOperationInProgress(false);
        }, GENERATE_VIEW_UPDATE_DELAY_MS);
        return () => clearTimeout(timer);
      }
    }, [isOperationInProgress, isPolling, generateViewData]);

    useEffect(() => {
      if (statusCheckTimeoutRef.current) {
        clearTimeout(statusCheckTimeoutRef.current);
        statusCheckTimeoutRef.current = null;
      }

      if (!isPolling) return;

      statusCheckTimeoutRef.current = setTimeout(() => {
        const latestStatus = statusData?.reProductionCurrentStatus;
        if (!latestStatus) return;

        const latestStatusCode =
          latestStatus.reProductionStatus ??
          latestStatus.reProductionStatusCode;
        const latestState = latestStatus.reProductionState;
        const statusKey = `${String(latestStatusCode)}-${String(latestState)}-${latestStatus.shouldContinue}-${latestStatus.isMarkComplete}`;

        if (lastProcessedStatusRef.current === statusKey) {
          return;
        }

        const shouldStop =
          latestStatusCode === ProductionStatus.FAILED ||
          latestStatusCode === ProductionStatus.COMPLETED ||
          latestState === ProductionState.QCCOMPLETE ||
          latestStatus.shouldContinue === true;

        if (shouldStop) {
          lastProcessedStatusRef.current = statusKey;

          setIsPolling(false);

          if (
            latestStatus.isMarkComplete ||
            latestState === ProductionState.QCCOMPLETE
          ) {
            onWizardStatusChange?.(
              latestState === ProductionState.QCCOMPLETE
                ? ''
                : ProductionStatus.COMPLETED
            );
            goNext?.();
          }
        }
      }, STATUS_CHECK_DELAY_MS);

      return () => {
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
          statusCheckTimeoutRef.current = null;
        }
      };
    }, [statusData, isPolling, goNext]);

    useEffect(() => {
      return () => {
        if (statusCheckTimeoutRef.current) {
          clearTimeout(statusCheckTimeoutRef.current);
        }
      };
    }, []);

    // Allow next for valid end/progress states, including extended list states during polling.
    const isNextDisabled =
      isSubmitting ||
      !isValid ||
      executeGenerateMutation.isPending ||
      executePlaceholderingMutation.isPending ||
      (!isStateAllowedWhilePolling && isPolling) ||
      !isValidStateForNext;

    const isAnyOperationInProgress =
      isOperationInProgress ||
      executeGenerateMutation.isPending ||
      executePlaceholderingMutation.isPending ||
      isPolling;

    useEffect(() => {
      onStateChange?.({
        saveDisabled: true,
        nextDisabled: isNextDisabled,
      });
    }, [isNextDisabled, onStateChange]);

    const buildExecutePayload = (
      overrides?: Partial<GetReProductionExecuteGenerateRequestBody>
    ) => {
      const d = productionGenerateDetails;
      return {
        reProductionId: reProductionId,
        moduleType: d?.moduleType || moduleType,
        isMarkComplete: d?.isMarkComplete || false,
        shouldContinue: d?.shouldContinue || false,
        canGenerate: d?.canGenerate || false,
        canRegenerate: d?.canRegenerate || false,
        securityGroupIdForProduction:
          d?.securityGroupIdForProduction || securityGroupId,
        productionType: d?.productionType || ProductionType.GENERATE,
        productionStateCode: d?.productionStateCode || '',
        productionTempararyFolderPath: d?.productionTempararyFolderPath || '',
        productionDestinationFolderPath:
          d?.productionDestinationFolderPath || '',
        productionFolderPath: d?.productionFolderPath || '',
        shouldPerformBlankPageDetection:
          d?.shouldPerformBlankPageDetection || false,
        regenerateType: d?.regenerateType ?? RegenerateType.RegenerateAll,
        ...(overrides || {}),
      };
    };

    const buildPlaceholderingPayload =
      (): GetReProductionExecutePlaceholderingRequestBody => {
        const d = productionGenerateDetails;
        return {
          reProductionId: reProductionId,
          moduleType: d?.moduleType || moduleType,
          isMarkComplete: d?.isMarkComplete || false,
          shouldContinue: d?.shouldContinue || false,
          canGenerate: d?.canGenerate || false,
          canRegenerate: d?.canRegenerate || false,
          securityGroupIdForProduction:
            d?.securityGroupIdForProduction || securityGroupId,
          productionType: d?.productionType || ProductionType.GENERATE,
          productionStateCode: d?.productionStateCode || '',
          productionTempararyFolderPath: d?.productionTempararyFolderPath || '',
          productionDestinationFolderPath:
            d?.productionDestinationFolderPath || '',
          productionFolderPath: d?.productionFolderPath || '',
          shouldPerformBlankPageDetection:
            d?.shouldPerformBlankPageDetection || false,
          regenerateType: d?.regenerateType ?? RegenerateType.RegenerateAll,
        };
      };

    const handleGenerate = () => {
      if (!productionGenerateDetails) return;
      const payload = buildExecutePayload();
      executeGenerateMutation.mutate(payload);
    };

    const handleRegenerate = () => {
      setShowRegenerateModal(true);
    };

    const handleRegenerateModalClose = () => {
      setShowRegenerateModal(false);
    };

    const handleRegenerateModalContinue = () => {
      if (!productionGenerateDetails) return;
      setShowRegenerateModal(false);
      const payload = buildExecutePayload({
        regenerateType: selectedRegenerateType,
      });
      executeGenerateMutation.mutate(payload);
    };

    const handlePlaceholderErrorDocsClick = () => {
      setShowPlaceholderErrorDocsModal(true);
    };

    const handlePlaceholderErrorDocsModalClose = () => {
      setShowPlaceholderErrorDocsModal(false);
    };

    const handlePlaceholderErrorDocsModalContinue = () => {
      if (!productionGenerateDetails) return;
      setShowPlaceholderErrorDocsModal(false);
      const payload = buildPlaceholderingPayload();
      executePlaceholderingMutation.mutate(payload);
    };

    const exportBatesParams: GetReProductionExportBatesRangeRequestParam = {
      reProductionId,
    };

    const {refetch: refetchExportBatesRange, isFetching: isExportingBates} =
      useGetReProductionExportBatesRange(exportBatesParams, false);

    const handleExportBates = async () => {
      const {error} = await refetchExportBatesRange();
      if (error) {
        getStatus?.(true, ToastType.Error, error?.message || '');
      } else {
        getStatus?.(
          true,
          ToastType.Success,
          t(
            'productionsAndExports.createNewProduction.generate.exportBatesSuccessMessage'
          )
        );
      }
    };

    const onSubmit = (data: ProductionGenerateFormData) => {
      if (data.action === FormAction.NEXT) {
        goNext?.();
      }
    };

    return (
      <>
        <Box
          className="re-production-generate"
          data-testid="reproduce-generate-screen"
        >
          <Box className="re-production-generate__header">
            <Typography
              id="re-generate-header"
              className="re-production-generate__header-title"
            >
              {t('reProduction.generate.headerText')}
            </Typography>
          </Box>

          <Box
            className="re-production-generate__body"
            component="form"
            onSubmit={handleSubmit(onSubmit)}
            role="form"
            aria-label={t('reProduction.generate.headerText')}
            aria-labelledby="re-generate-header"
          >
            <Box className="generate-info-section">
              <Box className="generate-info-content">
                <Typography
                  className="generate-production-name typo-text subhead1_size"
                  data-testid="reproduce-production-name"
                >
                  {productionGenerateDetails?.reProductionName || ''}
                </Typography>
                <Box className="generate-info-details">
                  <Typography
                    className="generate-info-label typo-text label1_size"
                    data-testid="reproduce-generate-status-label"
                  >
                    {t('reProduction.generate.statusLabel')}
                  </Typography>
                  <Typography
                    component="div"
                    className="generate-info-value typo-text label1_size"
                    data-testid="reproduce-generate-status"
                  >
                    {parsedStatus ? (
                      <>
                        {parsedStatus.statusCode === ProductionStatus.FAILED ? (
                          <>
                            {parsedStatus.mainText}:{' '}
                            <ArrButton
                              className="generate-status-button generate-status-button--failed"
                              onClick={() => {
                                setOpenErrorGridModal(true);
                                setGridType(ErrorGridType.ProductionErrors);
                              }}
                              dataTestId="btn-reproduceGenerate"
                            >
                              {parsedStatus.status}
                            </ArrButton>
                          </>
                        ) : (
                          <>
                            {parsedStatus.mainText}
                            {parsedStatus.status && (
                              <>
                                :{' '}
                                <span
                                  className={`${
                                    parsedStatus.statusCode ===
                                    ProductionStatus.COMPLETED
                                      ? 'text-success'
                                      : ''
                                  }`}
                                >
                                  {parsedStatus.status}
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      displayStatus
                    )}
                    <span className="generate-status-count">
                      {producitonDocsCompleteText &&
                        ` ${producitonDocsCompleteText}`}
                    </span>
                  </Typography>

                  <Typography className="generate-info-label typo-text label1_size">
                    {t('reProduction.generate.documentsGeneratedLabel')}
                  </Typography>
                  <Typography
                    component="div"
                    className="generate-info-value typo-text label1_size"
                    data-testid="reproduce-docs-generated"
                  >
                    {docsGenerated}
                  </Typography>

                  <Typography className="generate-info-label typo-text label1_size">
                    {t('reProduction.generate.priorProductionsLabel')}
                  </Typography>
                  <Typography
                    component="div"
                    className="generate-info-value typo-text label1_size"
                    data-testid="reproduce-prior-production-name"
                  >
                    {productionGenerateDetails?.priorProductionName}
                  </Typography>
                </Box>
              </Box>
              <Box className="generate-button-container">
                {isAnyOperationInProgress ? (
                  <ArrButton
                    variant="primary"
                    size="medium"
                    disabled={isAnyOperationInProgress}
                    dataTestId="generate-disabled-button"
                  >
                    {t('reProduction.generate.modals.regenerate.inProgress')}
                  </ArrButton>
                ) : (
                  <>
                    {canShowPlaceholderErrorDocsButton && (
                      <ArrButton
                        variant="secondary"
                        size="medium"
                        onClick={handlePlaceholderErrorDocsClick}
                        dataTestId="placeholder-error-docs-button"
                      >
                        {t(
                          'productionsAndExports.createNewProduction.generate.placeholderErrorDocsButton'
                        )}
                      </ArrButton>
                    )}
                    {canShowGenerateButton && (
                      <ArrButton
                        variant="primary"
                        size="medium"
                        onClick={handleGenerate}
                        dataTestId="generate-button"
                      >
                        {t('reProduction.generate.generateButton')}
                      </ArrButton>
                    )}
                    {shouldContinueGeneration && (
                      <ArrButton
                        variant="primary"
                        size="medium"
                        onClick={handleGenerate}
                        dataTestId="generate-button"
                      >
                        {t('reProduction.generate.continueGenerationButton')}
                      </ArrButton>
                    )}
                    {canShowRegenerateButton && (
                      <ArrButton
                        variant="primary"
                        size="medium"
                        onClick={() => {
                          setSelectedRegenerateType(
                            RegenerateType.RegenerateAll
                          );
                          handleRegenerate();
                        }}
                        disabled={isRegenerateDisabled}
                        dataTestId="regenerate-button"
                      >
                        {t('reProduction.generate.regenerateButton')}
                      </ArrButton>
                    )}
                  </>
                )}
              </Box>
            </Box>

            <Box className="generate-automated-check-section">
              <Box className="automated-check-buttons">
                <ArrButton
                  variant="secondary"
                  size="medium"
                  onClick={handleExportBates}
                  disabled={
                    !canShowExportBatesButton ||
                    isExportingBates ||
                    isInProgressOrCompleted
                  }
                  dataTestId="export-bates-button"
                >
                  {t(
                    'productionsAndExports.createNewProduction.generate.exportBatesButton'
                  )}
                </ArrButton>
              </Box>
              <Box className="automated-check-table">
                <Box className="automated-check-table-header">
                  <Box className="table-row">
                    <Box className="table-cell">
                      <Typography
                        className="typo-text label2_size"
                        data-testid="reproduce-automated-check-label"
                      >
                        {t('reProduction.generate.automatedCheckLabel')}
                      </Typography>
                    </Box>
                    <Box className="table-cell">
                      <Typography
                        className="typo-text label2_size"
                        data-testid="reproduce-automated-check-status-header"
                      >
                        {t('reProduction.generate.statusTableHeader')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Box className="automated-check-table-body">
                  {automatedChecks.length > 0 &&
                    automatedChecks.map((check, index) => (
                      <Box
                        className="table-row"
                        key={index}
                        data-testid={`reproduce-automated-check-row-${index}`}
                      >
                        <Box className="table-cell">
                          <Typography
                            className="typo-text body3_size"
                            data-testid={`reproduce-automated-check-name-${index}`}
                          >
                            {check.name}
                          </Typography>
                        </Box>
                        <Box className="table-cell">
                          <Typography
                            className={`typo-text body3_size ${check.className || ''}`}
                            data-testid={`reproduce-automated-check-status-${index}`}
                          >
                            {check.status || ''}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Box>
              </Box>
            </Box>

            {canShowNativelyPlaceholderErrorDocs && (
              <Box className="generate-bottom-section">
                <ArrButton
                  variant="text"
                  size="medium"
                  onClick={() => {
                    setOpenErrorGridModal(true);
                    setGridType(ErrorGridType.PlaceHolderErrors);
                  }}
                  dataTestId="natively-placeholder-error-docs-button"
                >
                  {t(
                    'productionsAndExports.createNewProduction.generate.viewNativelyPlaceholderErrorDocs',
                    {
                      count:
                        productionGenerateDetails?.nativelyGeneratedDocs || 0,
                    }
                  )}
                </ArrButton>
              </Box>
            )}
          </Box>
        </Box>

        <ArrModal
          showModal={showRegenerateModal}
          showClose={false}
          title={t('reProduction.generate.modals.regenerate.title')}
          description={t('reProduction.generate.modals.regenerate.description')}
          onClose={handleRegenerateModalClose}
          className="regenerate-production-modal"
          buttons={[
            {
              id: 'cancel',
              label: t('reProduction.generate.modals.regenerate.cancel'),
              variant: 'secondary',
              onClick: handleRegenerateModalClose,
              dataTestId: 'btn-reproduceGenerate-cancel',
            },
            {
              id: 'continue',
              label: t('reProduction.generate.modals.regenerate.continue'),
              variant: 'primary',
              onClick: handleRegenerateModalContinue,
              disabled: executeGenerateMutation.isPending,
              loading: executeGenerateMutation.isPending,
              dataTestId: 'btn-reproduceGenerate-continue',
            },
          ]}
          dataTestId="modal-reproduce-regenerate"
        >
          <Box className="regenerate-modal-content">
            <Box className="regenerate-options-section">
              <RadioGroup
                value={selectedRegenerateType}
                onChange={(e) => {
                  setSelectedRegenerateType(
                    Number(e.target.value) as RegenerateType
                  );
                }}
                className="custom-radio-group"
                aria-label={t('reProduction.generate.modals.regenerate.title')}
              >
                <FormControlLabel
                  value={RegenerateType.RegenerateAll}
                  control={
                    <Radio
                      icon={<RadioEnableIcon />}
                      checkedIcon={<RadioActiveIcon />}
                      className="typo-text label2_size radio-btn"
                      inputProps={{
                        'aria-label': t(
                          'reProduction.generate.modals.regenerate.option1.label'
                        ),
                      }}
                    />
                  }
                  className="typo-text label2_size radio-btn-container"
                  label={
                    <span className="typo-text label2_size radio-text">
                      {t(
                        'reProduction.generate.modals.regenerate.option1.label'
                      )}
                    </span>
                  }
                />
                <FormControlLabel
                  value={RegenerateType.RegenerateOnlyErrors}
                  control={
                    <Radio
                      icon={<RadioEnableIcon />}
                      checkedIcon={<RadioActiveIcon />}
                      className="typo-text label2_size radio-btn"
                      inputProps={{
                        'aria-label': t(
                          'reProduction.generate.modals.regenerate.option2.label'
                        ),
                      }}
                    />
                  }
                  className="typo-text label2_size radio-btn-container"
                  label={
                    <span className="typo-text label2_size radio-text">
                      {t(
                        'reProduction.generate.modals.regenerate.option2.label'
                      )}
                    </span>
                  }
                />
              </RadioGroup>
            </Box>
          </Box>
        </ArrModal>

        <ArrModal
          showModal={showPlaceholderErrorDocsModal}
          showClose={false}
          title={t(
            'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.title'
          )}
          description={t(
            'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.description'
          )}
          onClose={handlePlaceholderErrorDocsModalClose}
          className="placeholder-error-docs-modal"
          buttons={[
            {
              id: 'cancel',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.cancel'
              ),
              variant: 'secondary',
              onClick: handlePlaceholderErrorDocsModalClose,
              dataTestId: 'error-reproduceGenerate-cancel',
            },
            {
              id: 'continue',
              label: t(
                'productionsAndExports.createNewProduction.generate.modals.placeholderErrorDocs.continue'
              ),
              variant: 'primary',
              onClick: handlePlaceholderErrorDocsModalContinue,
              disabled: executePlaceholderingMutation.isPending,
              loading: executePlaceholderingMutation.isPending,
              dataTestId: 'error-reproduceGenerate-continue',
            },
          ]}
          dataTestId="modal-reproduce-placeholderErrorDocs"
        />
        {openErrorGridModal && (
          <ReProductionErrorGridModal
            productionId={reProductionId}
            securityGroupId={securityGroupId}
            open={openErrorGridModal}
            onClose={() => setOpenErrorGridModal(false)}
            title={t('reProduction.generate.modals.errors.reProductionErrors')}
            tooltip={t(
              'productionsAndExports.createNewProduction.generate.modals.errors.tooltip'
            )}
            gridType={gridType}
            productionName={productionGenerateDetails?.reProductionName}
          />
        )}
      </>
    );
  }
);

ReProductionGenerate.displayName = 'ReProductionGenerate';
export default ReProductionGenerate;
